import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import axios from "axios";
import { BASE_URL } from "../../utils/serverContextPath";
import { getLoggedInUser, hasRole } from "../../pages/userapp/ServiceUserApp";

export default function ForumTopicCard({
    topic,
    onDetail,
    onEdit,
    onDelete
}) {

    const { t } = useTranslation();
    const navigate = useNavigate();

    const currentUser = getLoggedInUser();
    const isAdmin = hasRole("ADMIN_ROLE");

    // ============================================================
    // Auteur
    // ============================================================
    const author = topic?.author || {};
    // ============================================================
    // Avatar par défaut
    // ============================================================

    const defaultAvatar =
        "https://cdn-icons-png.flaticon.com/512/847/847969.png";

    // ============================================================
    // Photo de profil
    // ============================================================


    const hasProfilePhoto =
        typeof author?.photoUrl === "string" &&
        author.photoUrl.trim() !== "";

    const photo =
        hasProfilePhoto
            ? `${BASE_URL}userapp/downloadprofile/${author.id}`
            : defaultAvatar;

    const photo2 = author?.photoUrl
        ? `${BASE_URL}userapp/downloadprofile/${author.id}`
        : defaultAvatar;



    // ============================================================
    // Autorisation modification
    // L'auteur peut modifier son propre sujet
    // ============================================================

    const canEdit =
        currentUser &&
        author?.id === currentUser?.id;

    // ============================================================
    // Fichier attaché au Topic
    // ============================================================

    const [attachedFileUrl, setAttachedFileUrl] =
        useState(null);

    const [attachedFileType, setAttachedFileType] =
        useState(null);

    const [loadingAttachedFile, setLoadingAttachedFile] =
        useState(false);

    const [attachedFileError, setAttachedFileError] =
        useState(false);


    const [videoThumbnail, setVideoThumbnail] = useState(null);
    const [videoThumbnailLoading, setVideoThumbnailLoading] = useState(false);
    const [videoThumbnailError, setVideoThumbnailError] = useState(false);

    // ============================================================
    // Chargement de attachedFile1
    // ============================================================

    useEffect(() => {

        let cancelled = false;

        const generateVideoThumbnail = async () => {

            // ----------------------------------------------------
            // Pas une vidéo
            // ----------------------------------------------------

            if (
                attachedFileType !== "video" ||
                !attachedFileUrl
            ) {
                setVideoThumbnail(null);
                return;
            }

            setVideoThumbnailLoading(true);
            setVideoThumbnailError(false);
            setVideoThumbnail(null);

            const video = document.createElement("video");

            video.preload = "metadata";
            video.muted = true;
            video.playsInline = true;

            // Important pour permettre canvas avec une URL distante
            video.crossOrigin = "anonymous";

            video.src = attachedFileUrl;

            try {

                // ------------------------------------------------
                // Attendre que les métadonnées soient disponibles
                // ------------------------------------------------

                await new Promise((resolve, reject) => {

                    video.onloadedmetadata = resolve;

                    video.onerror = () => {
                        reject(
                            new Error(
                                "Impossible de charger la vidéo"
                            )
                        );
                    };

                });

                // ------------------------------------------------
                // Aller à la première image
                // ------------------------------------------------

                await new Promise((resolve) => {

                    video.onseeked = resolve;

                    // 0.1 seconde évite parfois une première frame noire
                    video.currentTime = 0.1;

                });

                if (cancelled) {
                    return;
                }

                // ------------------------------------------------
                // Dimensions de la miniature
                // ------------------------------------------------

                const MAX_WIDTH = 640;

                const videoWidth = video.videoWidth;
                const videoHeight = video.videoHeight;

                if (!videoWidth || !videoHeight) {
                    throw new Error(
                        "Dimensions vidéo invalides"
                    );
                }

                const ratio =
                    Math.min(
                        1,
                        MAX_WIDTH / videoWidth
                    );

                const width =
                    Math.round(
                        videoWidth * ratio
                    );

                const height =
                    Math.round(
                        videoHeight * ratio
                    );

                // ------------------------------------------------
                // Canvas
                // ------------------------------------------------

                const canvas =
                    document.createElement("canvas");

                canvas.width = width;
                canvas.height = height;

                const context =
                    canvas.getContext("2d");

                context.drawImage(
                    video,
                    0,
                    0,
                    width,
                    height
                );

                // ------------------------------------------------
                // Compression JPEG
                // ------------------------------------------------

                const thumbnail =
                    canvas.toDataURL(
                        "image/jpeg",
                        0.75
                    );

                if (!cancelled) {
                    setVideoThumbnail(thumbnail);
                }

            } catch (error) {

                console.error(
                    "Erreur génération miniature vidéo :",
                    error
                );

                if (!cancelled) {
                    setVideoThumbnailError(true);
                }

            } finally {

                video.pause();
                video.removeAttribute("src");
                video.load();

                if (!cancelled) {
                    setVideoThumbnailLoading(false);
                }
            }
        };

        generateVideoThumbnail();

        return () => {
            cancelled = true;
        };

    }, [
        attachedFileUrl,
        attachedFileType
    ]);

    useEffect(() => {
        let objectUrl = null;
        const loadAttachedFile = async () => {
            // ----------------------------------------------------
            // Pas de fichier
            // ----------------------------------------------------
            if (!topic?.attachedFile1) {
                setAttachedFileUrl(null);
                setAttachedFileType(null);
                return;
            }

            try {

                setLoadingAttachedFile(true);
                setAttachedFileError(false);

                // ------------------------------------------------
                // Endpoint backend
                // ------------------------------------------------

                const url =
                    `${BASE_URL}forumtopic/downloadfile/${topic.id}/1`;

                const response = await axios.get(
                    url,
                    {
                        responseType: "blob"
                    }
                );

                // ------------------------------------------------
                // Type MIME retourné par le backend
                // ------------------------------------------------

                const contentType =
                    response.headers?.["content-type"] ||
                    response.data?.type ||
                    "";

                // ------------------------------------------------
                // Création de l'URL temporaire
                // ------------------------------------------------

                objectUrl = URL.createObjectURL(
                    new Blob(
                        [response.data],
                        {
                            type: contentType
                        }
                    )
                );

                setAttachedFileUrl(objectUrl);

                // ------------------------------------------------
                // Détermination du type
                // ------------------------------------------------

                if (
                    contentType.startsWith("image/")
                ) {

                    setAttachedFileType("image");

                } else if (
                    contentType.startsWith("video/")
                ) {

                    setAttachedFileType("video");

                } else {

                    setAttachedFileType("other");
                }

            } catch (error) {

                console.error(
                    "Erreur lors du chargement de attachedFile1 :",
                    error
                );

                setAttachedFileUrl(null);
                setAttachedFileType(null);
                setAttachedFileError(true);

            } finally {

                setLoadingAttachedFile(false);
            }
        };

        loadAttachedFile();

        // ========================================================
        // Nettoyage ObjectURL
        // ========================================================

        return () => {

            if (objectUrl) {

                URL.revokeObjectURL(objectUrl);
            }
        };

    }, [
        topic?.id,
        topic?.attachedFile1
    ]);

    // ============================================================
    // Suppression du topic
    // ============================================================

    const handleDelete = () => {

        // --------------------------------------------------------
        // Vérification ADMIN_ROLE
        // --------------------------------------------------------

        if (!isAdmin) {

            console.warn(
                "Suppression refusée : utilisateur non administrateur."
            );

            return;
        }

        // --------------------------------------------------------
        // Vérification topic
        // --------------------------------------------------------

        if (!topic?.id) {

            console.error(
                "Impossible de supprimer le sujet : ID absent."
            );

            return;
        }
        if (typeof onDelete === "function") {
            onDelete(topic.id);

        } else {

            console.error(
                "ForumTopicCard : onDelete n'est pas défini."
            );
        }
    };

    // ============================================================
    // Formatage date
    // ============================================================

    const formatDate = (dateString) => {
        if (!dateString) {
            return "-";
        }
        try {

            const [
                datePart,
                timePart
            ] = dateString.split(" ");

            if (!datePart) {

                return dateString;
            }

            const [
                day,
                month,
                year
            ] = datePart.split("/");

            let hour = 0;
            let minute = 0;
            let second = 0;

            if (timePart) {

                const timeValues =
                    timePart.split(":");

                hour =
                    parseInt(
                        timeValues[0] || 0,
                        10
                    );

                minute =
                    parseInt(
                        timeValues[1] || 0,
                        10
                    );

                second =
                    parseInt(
                        timeValues[2] || 0,
                        10
                    );
            }

            const date = new Date(
                parseInt(year, 10),
                parseInt(month, 10) - 1,
                parseInt(day, 10),
                hour,
                minute,
                second
            );

            if (
                isNaN(date.getTime())
            ) {

                return dateString;
            }

            return date.toLocaleDateString(
                "fr-FR",
                {
                    day: "2-digit",
                    month: "long",
                    year: "numeric",
                    hour: "2-digit",
                    minute: "2-digit"
                }
            );

        } catch (error) {

            console.error(
                "Erreur formatage date :",
                error
            );

            return dateString;
        }
    };

    // ============================================================
    // Ouvrir le fichier dans un nouvel onglet
    // ============================================================

    const openAttachedFile = () => {
        if (!attachedFileUrl) {
            return;
        }

        window.open(
            attachedFileUrl,
            "_blank",
            "noopener,noreferrer"
        );
    };

    // ============================================================
    // Rendu du fichier attaché
    // ============================================================

    const renderAttachedFile = () => {

        // --------------------------------------------------------
        // Aucun fichier
        // --------------------------------------------------------

        if (!topic?.attachedFile1) {
            return null;
        }

        // --------------------------------------------------------
        // Chargement
        // --------------------------------------------------------

        if (loadingAttachedFile) {

            return (
                <div className="text-center py-3">

                    <div
                        className="spinner-border text-success"
                        role="status"
                    >
                        <span className="visually-hidden">
                            Chargement...
                        </span>
                    </div>

                    <div className="small text-muted mt-2">
                        Chargement du fichier...
                    </div>

                </div>
            );
        }

        // --------------------------------------------------------
        // Erreur
        // --------------------------------------------------------

        if (attachedFileError) {

            return (
                <div className="alert alert-warning mt-3 mb-0">

                    <i className="bi bi-exclamation-triangle me-2"></i>

                    Impossible d'afficher le fichier attaché.

                </div>
            );
        }

        // --------------------------------------------------------
        // Pas encore d'URL
        // --------------------------------------------------------

        if (!attachedFileUrl) {
            return null;
        }

        // ========================================================
        // IMAGE
        // ========================================================

        if (attachedFileType === "image") {

            return (
                <div
                    className="forum-attached-file mt-3"
                    style={{
                        width: "100%",
                        maxWidth: "600px",
                        margin: "0 auto",
                        textAlign: "center",
                        backgroundColor: "#000",
                        borderRadius: "6px",
                        overflow: "hidden"
                    }}
                >

                    <img
                        src={attachedFileUrl}
                        alt={
                            topic?.topicObject ||
                            "Image attachée"
                        }
                        onClick={openAttachedFile}
                        loading="lazy"
                        style={{
                            display: "block",
                            width: "100%",
                            maxHeight: "350px",
                            objectFit: "contain",
                            cursor: "pointer",
                            backgroundColor: "#000"
                        }}
                    />

                </div>
            );
        }

        // ========================================================
        // VIDEO
        // ========================================================

        if (attachedFileType === "video") {

            return (
                <div
                    className="forum-attached-file mt-3"
                    onClick={openAttachedFile}
                    style={{
                        position: "relative",
                        width: "100%",
                        maxWidth: "650px",
                        height: "365px",
                        margin: "0 auto",
                        borderRadius: "10px",
                        overflow: "hidden",
                        backgroundColor: "#111",
                        cursor: "pointer",
                        boxShadow:
                            "0 2px 8px rgba(0,0,0,0.15)"
                    }}
                >

                    {/* ==================================================
                MINIATURE VIDEO
               ================================================== */}

                    {videoThumbnail && (

                        <img
                            src={videoThumbnail}
                            alt="Aperçu de la vidéo"
                            style={{
                                width: "100%",
                                height: "100%",
                                display: "block",
                                objectFit: "cover"
                            }}
                        />

                    )}

                    {/* ==================================================
                CHARGEMENT MINIATURE
               ================================================== */}

                    {videoThumbnailLoading && (

                        <div
                            style={{
                                position: "absolute",
                                inset: 0,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                backgroundColor: "#222"
                            }}
                        >

                            <div
                                className="spinner-border text-light"
                                role="status"
                            >
                                <span className="visually-hidden">
                                    Génération de l'aperçu...
                                </span>
                            </div>

                        </div>

                    )}

                    {/* ==================================================
                ERREUR MINIATURE
               ================================================== */}

                    {!videoThumbnailLoading &&
                        !videoThumbnail &&
                        videoThumbnailError && (

                            <div
                                style={{
                                    position: "absolute",
                                    inset: 0,
                                    display: "flex",
                                    flexDirection: "column",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    backgroundColor: "#222",
                                    color: "#fff"
                                }}
                            >

                                <i
                                    className="bi bi-camera-video"
                                    style={{
                                        fontSize: "55px"
                                    }}
                                />

                                <div className="mt-2">
                                    Vidéo
                                </div>

                            </div>

                        )}

                    {/* ==================================================
                BOUTON PLAY
               ================================================== */}

                    <div
                        style={{
                            position: "absolute",
                            inset: 0,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            background:
                                "linear-gradient(" +
                                "rgba(0,0,0,0.05)," +
                                "rgba(0,0,0,0.20)" +
                                ")",
                            pointerEvents: "none"
                        }}
                    >

                        <div
                            style={{
                                width: "75px",
                                height: "75px",
                                borderRadius: "50%",
                                backgroundColor:
                                    "rgba(220, 0, 0, 0.95)",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                boxShadow:
                                    "0 4px 15px rgba(0,0,0,0.45)"
                            }}
                        >

                            <i
                                className="bi bi-play-fill"
                                style={{
                                    color: "#fff",
                                    fontSize: "48px",
                                    marginLeft: "5px"
                                }}
                            />

                        </div>

                    </div>

                    {/* ==================================================
                INDICATION
               ================================================== */}

                    <div
                        style={{
                            position: "absolute",
                            bottom: 0,
                            left: 0,
                            right: 0,
                            padding: "15px",
                            color: "#fff",
                            fontSize: "14px",
                            textAlign: "center",
                            background:
                                "linear-gradient(" +
                                "transparent," +
                                "rgba(0,0,0,0.85)" +
                                ")",
                            pointerEvents: "none"
                        }}
                    >

                        <i className="bi bi-play-circle me-2"></i>

                        Cliquez pour visualiser la vidéo

                    </div>

                </div>
            );
        }

        // ========================================================
        // AUTRE TYPE DE FICHIER
        // ========================================================

        return (
            <div className="mt-3">

                <button
                    type="button"
                    className="btn btn-outline-primary"
                    onClick={openAttachedFile}
                >
                    <i className="bi bi-paperclip me-2"></i>

                    Fichier attaché

                </button>

            </div>
        );
    };

    // ============================================================
    // RENDER
    // ============================================================

    return (
        <div className="card shadow-sm forum-topic-card">
            <div className="card-body">
                <div className="row">
                    {/* ==================================================
                        AUTEUR
                    ================================================== */}
                    <div className="col-lg-2 text-center">
                        <img
                            src={photo}
                            alt={
                                author?.fullName ||
                                "Auteur"
                            }
                            className="rounded-circle shadow"
                            style={{
                                width: 90,
                                height: 90,
                                objectFit: "cover"
                            }}
                            onError={(e) => {

                                e.currentTarget.src =
                                    defaultAvatar;
                            }}
                        />

                        <h6 className="mt-3 mb-1">

                            {
                                author?.fullName ||
                                "-"
                            }

                        </h6>

                        <small className="text-muted">

                            {
                                formatDate(
                                    topic?.dateForumTopic
                                )
                            }

                        </small>

                    </div>


                    {/* ==================================================
                        SUJET
                    ================================================== */}

                    <div className="col-lg-7">

                        <h4 className="fw-bold text-success">

                            {
                                topic?.topicObject
                            }

                        </h4>


                        <p
                            className="text-muted"
                            style={{
                                textAlign: "justify",
                                lineHeight: "1.8"
                            }}
                        >

                            {
                                topic?.topicBody

                                    ? topic.topicBody.length > 350

                                        ? topic.topicBody.substring(
                                            0,
                                            350
                                        ) + "..."

                                        : topic.topicBody

                                    : "Aucune description."
                            }

                        </p>


                        {/* ==================================================
                            IMAGE / VIDEO ATTACHE
                        ================================================== */}

                        {
                            renderAttachedFile()
                        }


                        {/* ==================================================
                            BADGES
                        ================================================== */}
                        <div className="mt-3">
                            {
                                topic?.resolved && (
                                    <span className="badge bg-success me-2">
                                        <i className="bi bi-check-circle me-1"></i>
                                        Résolu
                                    </span>
                                )
                            }


                            {
                                topic?.pinned && (
                                    <span className="badge bg-warning text-dark me-2">
                                        <i className="bi bi-pin-angle-fill me-1"></i>
                                        Epinglé
                                    </span>
                                )
                            }

                        </div>

                    </div>


                    {/* ==================================================
                        STATISTIQUES + BOUTONS
                    ================================================== */}

                    <div className="col-lg-3">

                        {/* ==================================================
                            STATISTIQUES
                        ================================================== */}

                        <div className="row text-center">

                            <div className="col-4">

                                <h4 className="text-primary">

                                    {
                                        topic?.nbViews || 0
                                    }

                                </h4>

                                <small>

                                    {
                                        t("forum.views")
                                    }

                                </small>

                            </div>


                            <div className="col-4">

                                <h4 className="text-success">

                                    {
                                        topic?.nbLikes || 0
                                    }

                                </h4>

                                <small>
                                    {t("forum.likes")}
                                </small>
                            </div>


                            <div className="col-4">
                                <h4 className="text-danger">
                                    {topic?.nbMessage || 0}
                                </h4>

                                <small>
                                    {t("forum.replies")}
                                </small>
                            </div>
                        </div>
                        <hr />
                        {/* ==================================================
                            BOUTONS
                        ================================================== */}

                        <div className="d-grid gap-2">
                            {/* ------------------------------------------
                                OUVRIR
                            ------------------------------------------ */}
                            <button
                                type="button"
                                className="btn btn-success"
                                onClick={() =>
                                    navigate(
                                        `/forum/conversation/${topic.id}`
                                    )
                                }
                            >

                                <i className="bi bi-chat-left-text me-2"></i>
                                {t("forum.open")}
                            </button>


                            {/* ------------------------------------------
                                DETAILS
                            ------------------------------------------ */}

                            <button
                                type="button"
                                className="btn btn-outline-primary"
                                onClick={() =>
                                    onDetail(topic)
                                }
                            >
                                <i className="bi bi-eye me-2"></i>
                                {t("forum.details")}
                            </button>

                            {/* ------------------------------------------
                                MODIFIER - Topic Owner 
                            ------------------------------------------ */}
                            {
                                canEdit && (
                                    <button
                                        type="button"
                                        className="btn btn-outline-primary"
                                        onClick={() => onEdit(topic)}
                                        title={t("forum.editTopic")}
                                    >
                                        <i className="bi bi-trash me-2"></i>
                                        {t("button.edit")}
                                    </button>
                                )
                            }


                            {/* ------------------------------------------
                                SUPPRIMER - ADMIN UNIQUEMENT
                            ------------------------------------------ */}

                            {
                                isAdmin && (

                                    <button
                                        type="button"
                                        className="btn btn-outline-danger"
                                        onClick={handleDelete}
                                        title={t("forum.deleteTopic")}
                                    >

                                        <i className="bi bi-trash me-2"></i>

                                        {
                                            t("button.delete")
                                        }

                                    </button>
                                )
                            }
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}